import React, { useEffect, useState } from "react";
import { CircleX, Filter } from "lucide-react";
import { Button, Popover, OverlayTrigger, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useNavigate, useLocation } from "react-router-dom";

interface FilterButtonProps {
	urlParams?: URLSearchParams;
    onApplyFilter?: (filters: FilterState) => void;
    onResetFilter?: () => void;
}
interface FilterState {
    status: string;
    created: [start: Date | null, end: Date | null] | null;
    evaluation_date: [start: Date | null, end: Date | null] | null;
}

const popperConfig = {
    modifiers: [
      {
        name: "flip",
        options: {
          fallbackPlacements: ["top-end"]
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
        },
      },
    ],
};

const FilterButton = ({ onApplyFilter, onResetFilter, urlParams }: FilterButtonProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [show, setShow] = useState(false);
    const [filters, setFilters] = useState<FilterState>({
        created: null,
        evaluation_date: null,
        status: "",
    });

    const isFilterApplied = () => filters.created !== null || filters.evaluation_date !== null || filters.status !== "";

    const handleReset = (field?: keyof FilterState) => {
        if (field) {
            setFilters((prev) => ({ ...prev, [field]: field === "status" ? "" : null }));
        } else {
            setFilters({ created: null, evaluation_date: null, status: "" });
        }

        // TODO: quick fix to reset URL params
        // UseEffect auto cleans the url
        // if (filters.evaluation_date) {
		// 	urlParams?.delete("evaluation_start");
		// 	urlParams?.delete("evaluation_end");
        // }

        if (onResetFilter) {
            onResetFilter();
        }
    };

    const handleApplyFilter = () => {
        if (onApplyFilter) {
            onApplyFilter(filters);
        }
        setShow(false);
    };

    const handleSelect = (field: keyof FilterState, value: string) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
	};

	const parseDate = (value: string | null): Date | null => {
		return value ? new Date(value) : null;
    };

    // readback
    useEffect(() => {
        const status = urlParams?.get("status") || "";

        const created: [Date | null, Date | null] | null = [
            parseDate(urlParams?.get("created_start") as string),
            parseDate(urlParams?.get("created_end") as string),
        ];

        const evaluation_date: [Date | null, Date | null] | null = [
            parseDate(urlParams?.get("evaluation_start") as string) || null,
            parseDate(urlParams?.get("evaluation_end") as string) || null,
        ];

        const isParamsPresent = status || created[0] || created[1] || evaluation_date[0] || evaluation_date[1];

        setFilters({
            status,
            created: created[0] || created[1] ? created : null,
            evaluation_date: evaluation_date[0] || evaluation_date[1] ? evaluation_date : null,
        });

        // apply filter on page load
        if (onApplyFilter && isParamsPresent) {
            onApplyFilter({
                status,
                created: created || null,
                evaluation_date: evaluation_date || null,
            });
        }
    }, []);

    useEffect(() => {
        if (filters.status) {
            urlParams?.set("status", filters.status);
		} else {
			urlParams?.delete("status");
		}

		if (filters.created) {
			const [start, end] = filters.created;
            if(start) urlParams?.set("created_start", start.toISOString());
            if(end) urlParams?.set("created_end", end.toISOString());
		} else {
			urlParams?.delete("created_start");
			urlParams?.delete("created_end");
		}

        if (filters.evaluation_date) {
            const [start, end] = filters.evaluation_date;
            if (start) urlParams?.set("evaluation_start", start.toISOString());
            if (end) urlParams?.set("evaluation_end", end.toISOString());
        } else {
            urlParams?.delete("evaluation_start");
            urlParams?.delete("evaluation_end");
        }

        navigate({
            pathname: location.pathname,
            search: urlParams?.toString(),
        });
	}, [filters]);


    const popover = (
        <Popover id="filter-popover" className="filter-popover shadow-md">
            <Popover.Header className="bg-white border-bottom d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Filter by</span>
                <Button variant="link" className="p-0 text-dark" onClick={() => setShow(false)}>
                    <CircleX size={16} />
                </Button>
            </Popover.Header>
            <Popover.Body className="p-0">
                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold small">Created date range</span>

						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("created")}>
							Reset
						</Button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Group>
                            <Form.Label>From</Form.Label>
                            <DatePicker
                                id="date-due-from"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.created?.[0]}
                                onChange={(date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        created: [date as Date, prev.created?.[1] as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>To</Form.Label>
                            <DatePicker
                                id="date-due-to"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.created?.[1]}
                                onChange={(date: Date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        created: [prev.created?.[0] as Date, date as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                    </div>
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold small">Due date range</span>

						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("evaluation_date")}>
							Reset
						</Button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Group>
                            <Form.Label>From</Form.Label>
                            <DatePicker
                                id="date-due-from"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.evaluation_date?.[0]}
                                onChange={(date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        evaluation_date: [date as Date, prev.evaluation_date?.[1] as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>To</Form.Label>
                            <DatePicker
                                id="date-due-to"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.evaluation_date?.[1]}
                                onChange={(date: Date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        evaluation_date: [prev.evaluation_date?.[0] as Date, date as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                    </div>
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Status</span>
						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("status")}>
							Reset
						</Button>
                    </div>
                    <Form.Select className={filters.status ? "" : "placeholder-text"} value={filters.status} onChange={(e) => handleSelect("status", e.target.value)}>
                        <option value="" disabled>
                            Select one
                        </option>
                        <option value="all">All</option>
                        <option value="open">Open</option>
                        <option value="goal-met">Achieved</option>
                        <option value="goal-not-met">Unmet</option>
                    </Form.Select>
                </div>

                <div className="filter-actions d-flex gap-2 px-3 py-2">
                    <Button variant="outline-secondary" className="filter-actions__reset flex-grow-1 rounded-2" onClick={() => handleReset()}>
                        Reset All Filter
                    </Button>
                    <Button variant="light" className="filter-actions__apply flex-grow-1 rounded-2 text-dark" onClick={handleApplyFilter} disabled={!isFilterApplied()}>
                        Apply Filter
                    </Button>
                </div>
            </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom-end" flip={true} show={show} onToggle={setShow} overlay={popover} rootClose popperConfig={popperConfig}>
            <Button id="ap-filter-menu" variant="outline-secondary" className={`${show ? "active" : ""} filter-toggle bg-white text-dark border d-flex align-items-center gap-2`}>
				<Filter className={show ? "filled" : ""} />
                <span className="fw-medium small">Add Filter</span>
            </Button>
        </OverlayTrigger>
    );
};

export default FilterButton;